// Pre-filter content for blocked keywords before AI moderation
// This provides instant feedback and saves API calls

const BLOCKED_KEYWORDS = [
  // Politics
  'vote for', 'vote against', 'election', 'democrat', 'republican', 'liberal', 'conservative',
  'trump', 'biden', 'congress', 'senator', 'governor', 'maga', 'woke agenda',
  'left wing', 'right wing', 'socialism', 'communism', 'fascist', 'antifa',
  'political party', 'electoral', 'ballot', 'impeach', 'partisan',
  // Religion proselytizing
  'jesus saves', 'allah akbar', 'convert to', 'bible says', 'quran says',
  'accept jesus', 'accept christ', 'infidel', 'heathen', 'repent or',
  // Spam/Promotion
  'crypto', 'bitcoin', 'ethereum', 'nft drop', 'make money fast', 'dm me for',
  'link in bio', 'get rich', 'passive income', 'financial freedom scheme',
  'forex trading', 'dropshipping course', 'mlm', 'pyramid scheme',
  // Inappropriate
  'kill yourself', 'kys', 'neck yourself',
];

const BLOCKED_PATTERNS = [
  // Politics patterns
  /\b(vote|voting)\s+(for|against)\s+\w+/i,
  /\b(liberal|conservative)\s+(media|agenda|propaganda)\b/i,
  /\b(republican|democrat)(s)?\s+(are|suck|rule)\b/i,
  /\bmake\s+america\b/i,
  // Religious proselytizing
  /\b(accept|find)\s+(jesus|christ|god|allah)\b/i,
  /\bpraise\s+(be\s+to\s+)?(god|allah|jesus)\b/i,
  // Spam patterns  
  /\b(earn|make)\s+\$?\d+k?\s+(a|per)\s+(day|week|month)\b/i,
  /\bcheck\s+(out\s+)?my\s+(link|bio)\b/i,
  /\bfree\s+(money|crypto|bitcoin|nft)\b/i,
  // Harassment
  /\bgo\s+die\b/i,
  /\bkill\s+(your)?self\b/i,
];

// Sports-positive terms that can override some matches (context awareness)
const SPORTS_CONTEXT_TERMS = [
  'game', 'practice', 'training', 'coach', 'team', 'workout', 'recruit',
  'season', 'championship', 'tournament', 'tryout', 'scout', 'commitment',
  'highlight', 'stats', 'varsity', 'jv', 'junior varsity', 'ncaa', 'naia',
  'division', 'conference', 'playoff', 'championship', 'camp', 'showcase'
];

export interface ContentFilterResult {
  blocked: boolean;
  category?: 'politics' | 'religion' | 'spam' | 'harassment' | 'off_topic';
  reason?: string;
  matchedTerm?: string;
}

export function preFilterContent(text: string): ContentFilterResult {
  const lowerText = text.toLowerCase();
  
  // Check for blocked keywords
  for (const keyword of BLOCKED_KEYWORDS) {
    if (lowerText.includes(keyword.toLowerCase())) {
      const category = getCategoryForKeyword(keyword);
      return { 
        blocked: true, 
        category,
        reason: `Content may contain ${category} material`,
        matchedTerm: keyword 
      };
    }
  }
  
  // Check for blocked patterns
  for (const pattern of BLOCKED_PATTERNS) {
    const match = lowerText.match(pattern);
    if (match) {
      const category = getCategoryForPattern(pattern);
      return { 
        blocked: true, 
        category,
        reason: `Content may contain ${category} material`,
        matchedTerm: match[0]
      };
    }
  }
  
  return { blocked: false };
}

function getCategoryForKeyword(keyword: string): ContentFilterResult['category'] {
  const lowerKeyword = keyword.toLowerCase();
  
  // Politics keywords
  const politicsTerms = ['vote', 'election', 'democrat', 'republican', 'liberal', 'conservative', 
    'trump', 'biden', 'congress', 'senator', 'governor', 'maga', 'woke', 'left wing', 'right wing',
    'socialism', 'communism', 'fascist', 'antifa', 'political', 'electoral', 'ballot', 'impeach', 'partisan'];
  if (politicsTerms.some(term => lowerKeyword.includes(term))) return 'politics';
  
  // Religion keywords
  const religionTerms = ['jesus', 'allah', 'convert', 'bible', 'quran', 'christ', 'infidel', 'heathen', 'repent'];
  if (religionTerms.some(term => lowerKeyword.includes(term))) return 'religion';
  
  // Spam keywords
  const spamTerms = ['crypto', 'bitcoin', 'ethereum', 'nft', 'money', 'dm me', 'link in bio', 
    'rich', 'income', 'forex', 'dropship', 'mlm', 'pyramid'];
  if (spamTerms.some(term => lowerKeyword.includes(term))) return 'spam';
  
  // Harassment
  const harassmentTerms = ['kill', 'kys', 'neck yourself'];
  if (harassmentTerms.some(term => lowerKeyword.includes(term))) return 'harassment';
  
  return 'off_topic';
}

function getCategoryForPattern(pattern: RegExp): ContentFilterResult['category'] {
  const patternStr = pattern.source.toLowerCase();
  
  if (patternStr.includes('vote') || patternStr.includes('liberal') || 
      patternStr.includes('conservative') || patternStr.includes('republican') ||
      patternStr.includes('democrat') || patternStr.includes('america')) {
    return 'politics';
  }
  
  if (patternStr.includes('jesus') || patternStr.includes('christ') || 
      patternStr.includes('god') || patternStr.includes('allah') || patternStr.includes('praise')) {
    return 'religion';
  }
  
  if (patternStr.includes('earn') || patternStr.includes('make') || 
      patternStr.includes('link') || patternStr.includes('bio') ||
      patternStr.includes('crypto') || patternStr.includes('money')) {
    return 'spam';
  }
  
  if (patternStr.includes('kill') || patternStr.includes('die')) {
    return 'harassment';
  }
  
  return 'off_topic';
}

// Check if content has enough sports context to potentially be legitimate
export function hasSportsContext(text: string): boolean {
  const lowerText = text.toLowerCase();
  return SPORTS_CONTEXT_TERMS.some(term => lowerText.includes(term));
}
